import { useEffect, useState } from 'preact/hooks';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { ELEMENT_ORDER, SORCERY_CHART_COLORS } from '../../utils/sorcery/deckMetrics';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import StatCard from './StatCard';
import { CHART_NOISE_SVG_URL } from './ChartNoise';
import { t } from '../../localization/t';

const AXIS_TICK = { fill: TEXT_MUTED, fontSize: '0.6875cqw' };

/**
 * Custom shape for stacked cost bars: rounds top corners only on the
 * topmost non-zero segment of each stack. Lower segments render as
 * flat rectangles so their (otherwise rounded) tops don't peek out
 * from under the segment sitting on top.
 *
 * Recharts Bar `radius` prop applies uniformly to every rect in a
 * series, which is why we need per-payload control here.
 */
function StackedBarTopRounded({ fill, x, y, width, height, payload, element, canvasScale = 1 }) {
  if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(width) || width <= 0) return null;
  const isTop = payload?._topElement === element;
  const r = isTop ? Math.max(0, Math.min(6 * canvasScale, height, width / 2)) : 0;
  // The fractal-noise filter gives every bar a subtle paper grain so
  // it doesn't read as a flat vector fill.
  const grain = CHART_NOISE_SVG_URL;
  if (r === 0) {
    return <rect x={x} y={y} width={width} height={height} fill={fill} filter={grain} />;
  }
  const path =
    `M${x},${y + r} Q${x},${y} ${x + r},${y}` +
    ` L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r}` +
    ` L${x + width},${y + height} L${x},${y + height} Z`;
  return <path d={path} fill={fill} filter={grain} />;
}

/**
 * Rounded-top-corners bar shape for the single-series MinionPower
 * chart. Recharts' built-in `radius` prop renders as a simple
 * <path> fill, but since we want the same grainy filter as the
 * stacked cost bars we supply our own shape that paints a path with
 * `filter` applied.
 */
function NoisyBarTopRounded({ fill, x, y, width, height, canvasScale = 1 }) {
  if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(width) || width <= 0) return null;
  const r = Math.max(0, Math.min(6 * canvasScale, height, width / 2));
  if (r === 0) {
    return <rect x={x} y={y} width={width} height={height} fill={fill} filter={CHART_NOISE_SVG_URL} />;
  }
  const path =
    `M${x},${y + r} Q${x},${y} ${x + r},${y}` +
    ` L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r}` +
    ` L${x + width},${y + height} L${x},${y + height} Z`;
  return <path d={path} fill={fill} filter={CHART_NOISE_SVG_URL} />;
}

function formatShare(n) {
  if (!Number.isFinite(n)) return '0';
  // Integer shares are clean counts; fractional shares come from
  // multi-element cards splitting evenly across their elements.
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function CurveTooltip({ active, payload, label }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const rows = payload.filter((p) => p.value > 0);
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, r) => sum + (r.value || 0), 0);
  return (
    <div
      className="rounded-md px-3 py-2 text-xs backdrop-blur"
      style={{
        background: 'rgba(12, 10, 8, 0.95)',
        border: '1px solid rgba(180,140,60,0.3)',
        color: TEXT_PRIMARY,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}
    >
      <div className="text-[0.625cqw] mb-1" style={{ color: TEXT_MUTED }}>{t('Cost {n}', { n: label })}</div>
      {rows.map((r) => (
        <div key={r.dataKey} className="flex items-center gap-2 py-0.5">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.color }} />
          <span className="flex-1" style={{ color: TEXT_BODY }}>{t(r.dataKey)}</span>
          <span className="tabular-nums font-semibold">{formatShare(r.value)}</span>
        </div>
      ))}
      <div className="mt-1 pt-1 flex items-center gap-2 text-[0.6875cqw]" style={{ borderTop: '1px solid rgba(180,140,60,0.15)' }}>
        <span style={{ color: TEXT_MUTED }}>{t('Total')}</span>
        <span className="ml-auto tabular-nums" style={{ color: TEXT_PRIMARY }}>{formatShare(total)}</span>
      </div>
    </div>
  );
}

function PowerTooltip({ active, payload, label }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  return (
    <div
      className="rounded-md px-3 py-2 text-xs"
      style={{
        background: 'rgba(12, 10, 8, 0.95)',
        border: '1px solid rgba(180,140,60,0.3)',
        color: TEXT_PRIMARY,
      }}
    >
      <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Power {n}', { n: label })}</div>
      <div className="tabular-nums">{payload[0].value === 1 ? t('1 minion') : t('{n} minions', { n: payload[0].value })}</div>
    </div>
  );
}

/**
 * Headless mana-curve chart. The BarChart-and-axes innards of
 * ManaCurveCard with no StatCard frame and no fixed height. The parent
 * sizes it (`heightClass` or wrapping element). Used by both the deck
 * statistics overlay (via ManaCurveCard) and the draft footer (compact
 * inline placement).
 */
export function ManaCurveChart({ metrics, heightClass = 'h-56', emptyLabel = t('No spells in this deck yet.'), showReferenceLine = true, compact = false }) {
  const costStacked = metrics.charts.costStacked;
  const avgCost = metrics.averages.costNumeric;
  const empty = !costStacked || costStacked.every((row) => row.total === 0);
  // Recharts measures margins, axis widths and bar geometry in raw px,
  // so they don't track the cqw-driven container as the canvas resizes.
  // Scale them by the canvas factor to keep proportions consistent.
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);

  if (empty) {
    return (
      <div className={`${heightClass} flex items-center justify-center text-sm`} style={{ color: TEXT_BODY, opacity: 0.7 }}>
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={heightClass}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={costStacked}
          // `costStacked` only includes cost buckets that actually have
          // cards (see deckMetrics. Empty buckets are dropped). With a
          // single populated bucket recharts would otherwise stretch
          // that bar across the whole chart and read as a misleading
          // slab. maxBarSize caps the visual width so small datasets
          // stay legible without changing the underlying data shape.
          maxBarSize={48 * canvasScale}
          margin={{
            top: (showReferenceLine ? 26 : 8) * canvasScale,
            right: 8 * canvasScale,
            bottom: 0,
            // The StatCard variant uses a negative left margin to claw
            // back the slack inside its panel padding. In compact use
            // (e.g. the draft footer) the container has tight rounded
            // edges + overflow-hidden, so a negative margin pushes the
            // YAxis labels off the visible edge. Keep it at zero.
            left: (compact ? 0 : -18) * canvasScale,
          }}
        >
          <CartesianGrid vertical={false} stroke="rgba(180,140,60,0.08)" />
          <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'rgba(180,140,60,0.15)' }} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={28 * canvasScale} />
          <Tooltip cursor={{ fill: 'rgba(180,140,60,0.08)' }} content={<CurveTooltip />} />
          {[...ELEMENT_ORDER, 'Neutral'].map((el) => (
            <Bar
              key={el}
              dataKey={el}
              stackId="cost"
              fill={SORCERY_CHART_COLORS[el] || SORCERY_CHART_COLORS.Neutral}
              shape={(props) => <StackedBarTopRounded {...props} element={el} canvasScale={canvasScale} />}
            />
          ))}
          {(() => {
            if (!showReferenceLine || avgCost <= 0) return null;
            const target = costStacked.find((row) => Number(row.label) === Math.round(avgCost));
            if (!target) return null;
            return (
              <ReferenceLine
                x={target.label}
                stroke={SORCERY_CHART_COLORS.Neutral}
                strokeDasharray="3 3"
                label={{
                  value: t('avg {n}', { n: avgCost.toFixed(1) }),
                  position: 'top',
                  fill: SORCERY_CHART_COLORS.Neutral,
                  fontSize: '0.625cqw',
                }}
              />
            );
          })()}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ManaCurveCard({ metrics, className }) {
  return (
    <StatCard title={t('Mana Curve')} description={t('Spellbook cards by printed cost, stacked by element.')} className={className}>
      <ManaCurveChart metrics={metrics} />
    </StatCard>
  );
}

export function MinionPowerCard({ metrics, className }) {
  const power = metrics.charts.power;
  const empty = !power || power.length === 0;
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);

  return (
    <StatCard title={t('Minion Power')} description={t('Distribution of minion attack values.')} className={className}>
      {empty ? (
        <div className="h-48 flex items-center justify-center text-sm" style={{ color: TEXT_BODY, opacity: 0.7 }}>
          {t('No minions in this deck yet.')}
        </div>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={power}
              margin={{ top: 10 * canvasScale, right: 8 * canvasScale, bottom: 0, left: -18 * canvasScale }}
            >
              <CartesianGrid vertical={false} stroke="rgba(180,140,60,0.08)" />
              <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'rgba(180,140,60,0.15)' }} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={28 * canvasScale} />
              <Tooltip cursor={{ fill: 'rgba(180,140,60,0.08)' }} content={<PowerTooltip />} />
              <Bar
                dataKey="value"
                fill={SORCERY_CHART_COLORS.Neutral}
                shape={(props) => <NoisyBarTopRounded {...props} canvasScale={canvasScale} />}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </StatCard>
  );
}
